import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
    FiFileText,
    FiDownload,
    FiEye,
    FiBookmark,
    FiCalendar,
    FiUser,
    FiLock,
    FiUnlock,
    FiStar,
    FiSearch,
    FiFilter,
} from "react-icons/fi";

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export default function ReportsLibrary() {
    const [subscriptionPlan, setSubscriptionPlan] = useState("free"); // free, basic, premium
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [bookmarkedReports, setBookmarkedReports] = useState([]);
    const [viewingReport, setViewingReport] = useState(null);

    // Static Data - Research Reports
    const reports = [
        {
            id: 1,
            title: "Q4 2024 Market Outlook Report",
            category: "Market Analysis",
            author: "Research Team",
            date: "2024-01-10",
            size: "2.5 MB",
            pages: 45,
            isPremium: false,
            downloadCount: 120,
            description: "Comprehensive analysis of market trends and investment opportunities for Q4 2024.",
        },
        {
            id: 2,
            title: "Technology Sector Deep Dive",
            category: "Sector Analysis",
            author: "Senior Technology Analyst",
            date: "2024-01-08",
            size: "3.2 MB",
            pages: 62,
            isPremium: true,
            downloadCount: 89,
            description: "In-depth analysis of technology sector performance, trends, and future outlook.",
        },
        {
            id: 3,
            title: "Portfolio Rebalancing Strategy 2024",
            category: "Portfolio Strategy",
            author: "Investment Advisor",
            date: "2024-01-05",
            size: "1.8 MB",
            pages: 32,
            isPremium: true,
            downloadCount: 156,
            description: "Strategic recommendations for portfolio rebalancing in the new year.",
        },
        {
            id: 4,
            title: "Banking Sector Performance Review",
            category: "Sector Analysis",
            author: "Banking Sector Analyst",
            date: "2024-01-03",
            size: "2.1 MB",
            pages: 38,
            isPremium: false,
            downloadCount: 98,
            description: "Review of banking sector performance and key investment themes.",
        },
        {
            id: 5,
            title: "ESG Investing Guide 2024",
            category: "Investment Strategy",
            author: "ESG Research Team",
            date: "2023-12-28",
            size: "4.5 MB",
            pages: 78,
            isPremium: true,
            downloadCount: 67,
            description: "Complete guide to ESG investing strategies and opportunities.",
        },
        {
            id: 6,
            title: "Small Cap Opportunities Analysis",
            category: "Market Analysis",
            author: "Equity Research Team",
            date: "2023-12-25",
            size: "2.8 MB",
            pages: 52,
            isPremium: false,
            downloadCount: 134,
            description: "Analysis of small cap investment opportunities and risks.",
        },
    ];

    const categories = ["all", "Market Analysis", "Sector Analysis", "Portfolio Strategy", "Investment Strategy"];

    const canAccessContent = (isPremium) => {
        if (!isPremium) return true;
        return subscriptionPlan !== "free";
    };

    const handleBookmark = (reportId) => {
        if (bookmarkedReports.includes(reportId)) {
            setBookmarkedReports(bookmarkedReports.filter((id) => id !== reportId));
        } else {
            setBookmarkedReports([...bookmarkedReports, reportId]);
        }
    };

    const handleView = (report) => {
        if (report.isPremium && !canAccessContent(report.isPremium)) {
            alert("This report requires a premium subscription. Please upgrade your plan.");
            return;
        }
        setViewingReport(report);
        alert(`Opening report viewer for: ${report.title}`);
    };

    const handleDownload = (report) => {
        if (report.isPremium && !canAccessContent(report.isPremium)) {
            alert("This report requires a premium subscription. Please upgrade your plan.");
            return;
        }
        alert(`Downloading report: ${report.title} (Watermarked with user ID)`);
    };

    const filteredReports = reports.filter((report) => {
        const matchesSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            report.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === "all" || report.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
            <style>{`
                .report-card {
                    transition: all 0.3s ease;
                    border: 1px solid #e9ecef;
                }
                .report-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.1) !important;
                    border-color: #1bafb4;
                }
            `}</style>

                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div>


                                    <h3 className="text-primary mb-2">Reports Library</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">

                                                <NavLink to="/overview">Research Analysis</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item active">Reports Library</li>
                                        </ol>
                                    </nav>

                                </div>
                                <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                                    {subscriptionPlan === "free" && (
                                        <button
                                            className="btn btn-primary btn-sm fw-semibold"
                                            onClick={() => window.location.href = "/research/plans-billing"}
                                            style={{ borderRadius: "8px" }}
                                        >
                                            {/* <FiStar className="me-1" /> */}
                                            Upgrade Plan
                                        </button>
                                    )}
                                    <span
                                        className={`badge px-3 py-2 fw-semibold ${subscriptionPlan === "free"
                                            ? "bg-secondary"
                                            : subscriptionPlan === "basic"
                                                ? "bg-info"
                                                : "bg-success"
                                            }`}
                                        style={{ fontSize: "12px" }}
                                    >
                                        {subscriptionPlan === "free"
                                            ? "Free Plan"
                                            : subscriptionPlan === "basic"
                                                ? "Basic Plan"
                                                : "Premium Plan"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div
                            className="card-header border-0 py-3 px-4"
                            style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
                        >
                            <div className="d-flex align-items-center justify-content-between flex-wrap">
                                <div className="d-flex align-items-center">
                                    <div
                                        className="me-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "10px",
                                            background: "#c8eeecbf",
                                        }}
                                    >
                                        <FiSearch style={{ color: "#39b2b4", fontSize: "20px" }} />
                                    </div>
                                    <h6 className="mb-0 fw-bold text-dark">Search & Filter Reports</h6>
                                </div>
                                <div className="mt-2 mt-md-0">
                                    <span className="badge bg-light text-dark px-3 py-2 fw-semibold" style={{ fontSize: "12px" }}>
                                        {filteredReports.length} {filteredReports.length === 1 ? "Report" : "Reports"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-4">
                            <div className="row g-3">
                                <div className="col-md-8">
                                    <div className="input-group">
                                        <span
                                            className="input-group-text border-end-0"
                                            style={{
                                                backgroundColor: "#ccf1f7",
                                                borderColor: "#b3eaf4",
                                                borderRadius: "10px 0 0 10px",
                                            }}
                                        >
                                            <FiSearch style={{ color: "#006e82", fontSize: "18px" }} />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search reports by title or description..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{
                                                borderRadius: "0 10px 10px 0",
                                                borderColor: "#b3eaf4",
                                                fontSize: "14px",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = "#006e82";
                                                e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = "#b3eaf4";
                                                e.target.style.boxShadow = "none";
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <select
                                        className="form-select"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        style={{
                                            borderRadius: "10px",
                                            borderColor: "#b3eaf4",
                                            fontSize: "14px",
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = "#006e82";
                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = "#b3eaf4";
                                            e.target.style.boxShadow = "none";
                                        }}
                                    >
                                        {categories.map((category) => (
                                            <option key={category} value={category}>
                                                {category === "all" ? "All Categories" : category}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subscription Warning */}
                    {subscriptionPlan === "free" && (
                        <div
                            className="alert mb-4 border-0"
                            style={{
                                borderRadius: "12px",
                                backgroundColor: "#ccf1f7",
                                borderColor: "#b3eaf4",
                                border: "1px solid #b3eaf4",
                                color: "#006e82",
                            }}
                        >
                            <div className="d-flex align-items-start">
                                <FiLock className="me-3 mt-1" style={{ fontSize: "24px", color: "#006e82" }} />
                                <div className="flex-grow-1">
                                    <strong style={{ fontSize: "16px", color: "#006e82" }}>
                                        Premium Reports Limited
                                    </strong>
                                    <div className="mt-2" style={{ fontSize: "14px", color: "#006e82", opacity: 0.9 }}>
                                        Upgrade your plan to access all premium reports. Free plan users can view reports with watermarks.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reports Grid */}
                    <div className="row g-4">
                        {filteredReports.map((report) => {
                            const isLocked = report.isPremium && !canAccessContent(report.isPremium);
                            const isBookmarked = bookmarkedReports.includes(report.id);

                            return (
                                <div key={report.id} className="col-lg-6 col-md-6">
                                    <div
                                        className="card report-card border-0 shadow-sm h-100"
                                        style={{
                                            borderRadius: "12px",
                                            opacity: isLocked ? 0.85 : 1,
                                            border: "1px solid #e9ecef",
                                            transition: "all 0.3s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                            e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.1)";
                                            e.currentTarget.style.borderColor = "#b3eaf4";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
                                            e.currentTarget.style.borderColor = "#e9ecef";
                                        }}
                                    >
                                        <div className="card-body p-4">
                                            {/* Header */}
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="flex-grow-1">
                                                    {/* Badges */}
                                                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                                                        <span
                                                            className="badge px-3 py-2 fw-bold"
                                                            style={{
                                                                fontSize: "12px",
                                                                backgroundColor: "#39b2b4",
                                                                color: "white",
                                                                borderRadius: "6px",
                                                            }}
                                                        >
                                                            {report.category}
                                                        </span>
                                                        {report.isPremium && (
                                                            <span
                                                                className="badge px-3 py-2 fw-bold"
                                                                style={{
                                                                    background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                                                                    fontSize: "11px",
                                                                    color: "white",
                                                                    borderRadius: "6px",
                                                                }}
                                                            >
                                                                {isLocked ? (
                                                                    <>
                                                                        {/* <FiLock className="me-1" style={{ fontSize: "10px" }} /> */}
                                                                        Premium
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {/* <FiUnlock className="me-1" style={{ fontSize: "10px" }} /> */}
                                                                        Premium
                                                                    </>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Title */}
                                                    <h5
                                                        className="mb-3 fw-bold"
                                                        style={{ fontSize: "20px", color: "#006e82", lineHeight: "1.4" }}
                                                    >
                                                        {report.title}
                                                    </h5>

                                                    {/* Description */}
                                                    <p className="mb-3" style={{ fontSize: "14px", color: "#64748b", lineHeight: "1.6" }}>
                                                        {report.description}
                                                    </p>
                                                </div>
                                                <button
                                                    className={`btn btn-sm ${isBookmarked ? "btn-warning" : "btn-outline-secondary"}`}
                                                    onClick={() => handleBookmark(report.id)}
                                                    title="Bookmark"
                                                    style={{
                                                        borderRadius: "8px",
                                                        width: "40px",
                                                        height: "40px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        padding: 0,
                                                    }}
                                                >
                                                    <FiBookmark style={{ fontSize: "18px" }} />
                                                </button>
                                            </div>

                                            {/* Report Details */}
                                            <div className="d-flex align-items-center gap-4 mb-3 flex-wrap">
                                                <div className="d-flex align-items-center" style={{ color: "#64748b" }}>
                                                    <FiUser className="me-2" style={{ fontSize: "16px", color: "#94a3b8" }} />
                                                    <small style={{ fontSize: "13px" }}>{report.author}</small>
                                                </div>
                                                <div className="d-flex align-items-center" style={{ color: "#64748b" }}>
                                                    <FiCalendar className="me-2" style={{ fontSize: "16px", color: "#94a3b8" }} />
                                                    <small style={{ fontSize: "13px" }}>{formatDate(report.date)}</small>
                                                </div>
                                                <div className="d-flex align-items-center" style={{ color: "#64748b" }}>
                                                    <FiFileText className="me-2" style={{ fontSize: "16px", color: "#94a3b8" }} />
                                                    <small style={{ fontSize: "13px" }}>{report.pages} pages</small>
                                                </div>
                                            </div>

                                            {/* Size and Downloads */}
                                            <div
                                                className="d-flex align-items-center gap-4 mb-4 p-3 rounded"
                                                style={{
                                                    backgroundColor: "#ccf1f7",
                                                    borderRadius: "10px",
                                                    border: "1px solid #b3eaf4",
                                                }}
                                            >
                                                <div style={{ color: "#006e82" }}>
                                                    <strong style={{ fontSize: "13px" }}>Size:</strong>{" "}
                                                    <span style={{ fontSize: "13px" }}>{report.size}</span>
                                                </div>
                                                <div style={{ color: "#006e82" }}>
                                                    <strong style={{ fontSize: "13px" }}>Downloads:</strong>{" "}
                                                    <span style={{ fontSize: "13px" }}>{report.downloadCount}</span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="d-flex gap-2 mt-4 pt-3 border-top">
                                                {isLocked ? (
                                                    <button
                                                        className="btn btn-warning fw-semibold"
                                                        onClick={() => window.location.href = "/research/plans-billing"}
                                                        style={{
                                                            borderRadius: "10px",
                                                            padding: "15px",
                                                            fontSize: "14px",
                                                        }}
                                                    >
                                                        <FiLock className="" />
                                                        {/* Unlock with Premium */}
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="btn fw-semibold"
                                                            onClick={() => handleView(report)}
                                                            style={{
                                                                borderRadius: "10px",
                                                                padding: "15px",
                                                                fontSize: "14px",
                                                                backgroundColor: "#39b2b4",
                                                                color: "white",
                                                                border: "none",
                                                            }}
                                                        >
                                                            <FiEye className="" />
                                                            {/* View */}
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-primary fw-semibold"
                                                            onClick={() => handleDownload(report)}
                                                            title="Download (Watermarked)"
                                                            style={{
                                                                borderRadius: "10px",
                                                                padding: "15px",
                                                                fontSize: "14px",
                                                                borderColor: "#39b2b4",
                                                                color: "#fff",
                                                            }}
                                                        >
                                                            <FiDownload className="" />
                                                            {/* Download */}
                                                        </button>
                                                    </>
                                                )}
                                            </div>

                                            {/* Watermark Notice */}
                                            {!isLocked && subscriptionPlan === "free" && (
                                                <div
                                                    className="alert mt-3 mb-0 py-2 border-0"
                                                    style={{
                                                        borderRadius: "8px",
                                                        fontSize: "12px",
                                                        backgroundColor: "#ccf1f7",
                                                        border: "1px solid #b3eaf4",
                                                        color: "#006e82",
                                                    }}
                                                >
                                                    <small>
                                                        <strong>Note:</strong> Downloaded reports will be watermarked with your user ID for security purposes.
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Link Expiry Notice */}
                    <div
                        className="alert mt-4 border-0"
                        style={{
                            borderRadius: "12px",
                            backgroundColor: "#ccf1f7",
                            border: "1px solid #b3eaf4",
                            color: "#006e82",
                        }}
                    >
                        <div className="d-flex align-items-start">
                            <FiLock className="me-3 mt-1" style={{ fontSize: "24px", color: "#006e82" }} />
                            <div>
                                <strong style={{ fontSize: "16px", color: "#006e82" }}>Link Expiry</strong>
                                <div className="mt-2" style={{ fontSize: "14px", color: "#006e82", opacity: 0.9 }}>
                                    Report download links expire after 24 hours for security. Please download reports promptly.
                                </div>
                            </div>
                        </div>
                    </div>
             
       
        </div>
        </>
    );
}
